/**
 * NewTicketPage - the customer's "submit a ticket" form (SPEC §8).
 *
 * Fields:
 *  - Subject (required, <= 500 chars)
 *  - Description (TipTap, required, <= 50,000 chars)
 *  - Priority (Low / Medium / High; no Urgent - agent-only)
 *  - Type (Question / Service Request / Problem)
 *  - Attachments (dropzone, see TODO in component)
 *  - CC emails (comma-separated, RFC-5322ish validation)
 *
 * The form trusts the server to enforce its own restrictions; we just
 * keep the UI from sending Urgent and from sending invalid CC strings.
 */
import { useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { Alert, Button, Card, Input, Select } from "@/components/ui";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useCreateTicket } from "@/hooks/useCreateTicket";
import { useCreateTicketAsAgent } from "@/hooks/useCreateTicketAsAgent";
import { useAuthStore } from "@/stores/auth";
import type { TicketPriority, TicketType } from "@/types/api";

import { AttachmentDropzone } from "@/components/tickets/AttachmentDropzone";
import { RichTextEditor } from "@/components/tickets/RichTextEditor";
import { TicketOwnerPicker } from "@/components/tickets/TicketOwnerPicker";

const PRIORITY_OPTIONS: { value: TicketPriority; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

// Customer-selectable types only, sorted alphabetically. Must stay in
// sync with customer_visible_types in backend tickets_customer.py.
const TYPE_OPTIONS: { value: TicketType; label: string }[] = [
  { value: "Problem", label: "Problem" },
  { value: "Question", label: "Question" },
  { value: "Service Request", label: "Service Request" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUBJECT_MAX = 500;
const DESCRIPTION_MAX = 50_000;

function parseCcList(raw: string): { emails: string[]; invalid: string[] } {
  const emails: string[] = [];
  const invalid: string[] = [];
  for (const piece of raw.split(",")) {
    const trimmed = piece.trim();
    if (!trimmed) continue;
    if (EMAIL_RE.test(trimmed)) {
      emails.push(trimmed);
    } else {
      invalid.push(trimmed);
    }
  }
  return { emails, invalid };
}

export function NewTicketPage(): JSX.Element {
  usePageTitle("New Ticket - Buyist Support");

  const navigate = useNavigate();
  const createTicket = useCreateTicket();
  const createTicketAsAgent = useCreateTicketAsAgent();

  // Same page is mounted at /tickets/new for every role - customers
  // file their own tickets, agents file on a customer's behalf. Branch
  // the submit path on isAgent so customers go through the customer
  // endpoint (which derives the requester from the JWT) and agents go
  // through /agent/tickets with an explicit requester from the picker.
  const authUser = useAuthStore((s) => s.user);
  const isAgent = authUser?.isAgent ?? false;

  // Owner identity for agent submissions. Defaults to the agent
  // themselves so "file a ticket for myself" stays a single click.
  // Customer submissions ignore these - the server derives requester
  // from the JWT.
  const [ownerId, setOwnerId] = useState<string | null>(
    authUser?.id ?? null,
  );
  const [ownerEmail, setOwnerEmail] = useState<string>(
    authUser?.email ?? "",
  );
  const [ownerName, setOwnerName] = useState<string>(
    authUser?.name ?? "",
  );

  const [subject, setSubject] = useState("");
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [descriptionText, setDescriptionText] = useState("");
  const [priority, setPriority] = useState<TicketPriority>("medium");
  const [type, setType] = useState<TicketType | "">("");
  const [ccRaw, setCcRaw] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [subjectError, setSubjectError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(
    null,
  );
  const [ccError, setCcError] = useState<string | null>(null);
  const [ownerError, setOwnerError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const ccParsed = useMemo(() => parseCcList(ccRaw), [ccRaw]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitError(null);
    let hasError = false;

    const trimmedSubject = subject.trim();
    if (!trimmedSubject) {
      setSubjectError("Subject is required.");
      hasError = true;
    } else if (trimmedSubject.length > SUBJECT_MAX) {
      setSubjectError(`Subject must be ${SUBJECT_MAX} characters or less.`);
      hasError = true;
    } else {
      setSubjectError(null);
    }

    const trimmedDescription = descriptionText.trim();
    if (!trimmedDescription) {
      setDescriptionError("Description is required.");
      hasError = true;
    } else if (trimmedDescription.length > DESCRIPTION_MAX) {
      setDescriptionError(
        `Description must be ${DESCRIPTION_MAX} characters or less.`,
      );
      hasError = true;
    } else {
      setDescriptionError(null);
    }

    if (ccParsed.invalid.length > 0) {
      setCcError(
        `Invalid email${
          ccParsed.invalid.length === 1 ? "" : "s"
        }: ${ccParsed.invalid.join(", ")}`,
      );
      hasError = true;
    } else {
      setCcError(null);
    }

    // Agent submissions require a valid requester email + name (the
    // backend's AgentTicketCreate schema enforces both). The picker
    // emits all three fields together so this is just a defensive
    // check for the "agent cleared the picker somehow" edge case.
    if (isAgent && (!ownerEmail.trim() || !ownerName.trim())) {
      setOwnerError("Pick a customer to file this ticket on behalf of.");
      hasError = true;
    } else {
      setOwnerError(null);
    }

    if (hasError) return;

    try {
      // TODO (Phase 8): wire file uploads through multipart once the
      // attachment upload service is in place. Today we drop the files
      // here and let the customer re-attach in their first reply.
      const ticket = isAgent
        ? await createTicketAsAgent.mutateAsync({
            subject: trimmedSubject,
            description_text: trimmedDescription,
            description_html: descriptionHtml || null,
            priority,
            type: type || null,
            cc_emails: ccParsed.emails,
            requester_email: ownerEmail,
            requester_name: ownerName,
            requester_user_id: ownerId,
          })
        : await createTicket.mutateAsync({
            subject: trimmedSubject,
            description_text: trimmedDescription,
            description_html: descriptionHtml || null,
            priority,
            type: type || null,
            cc_emails: ccParsed.emails,
          });
      navigate(`/tickets/${ticket.public_code.toLowerCase()}`);
    } catch (err: unknown) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to create ticket.",
      );
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <i
          className="fa-duotone fa-thin fa-circle-plus text-3xl text-primary-600"
          aria-hidden="true"
        />
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">
            New Ticket
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            Tell us what's going on. A Buyist Support agent will respond as
            soon as possible.
          </p>
        </div>
      </div>

      <Card>
        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          {/* Agent-only owner picker. Sits FIRST in the form so the
              agent decides "who is this for" before typing the
              subject. Defaults to the agent themselves (preserves the
              previous behavior for self-filed tickets) and lets them
              search the customer directory. Customer-side mounts of
              this same page skip the picker entirely - the server
              derives the requester from the JWT. */}
          {isAgent && (
            <div>
              <TicketOwnerPicker
                id="ticket-owner"
                label="Ticket Owner"
                value={{ id: ownerId, email: ownerEmail, name: ownerName }}
                onChange={(next) => {
                  setOwnerId(next.id);
                  setOwnerEmail(next.email);
                  setOwnerName(next.name);
                  setOwnerError(null);
                }}
              />
              {ownerError && (
                <p className="mt-1 text-xs text-red-600">{ownerError}</p>
              )}
              <p className="mt-1 text-xs text-neutral-500">
                Search the customer directory. Defaults to you; pick
                someone else to file this ticket on their behalf.
              </p>
            </div>
          )}

          <Input
            id="ticket-subject"
            label="Subject"
            placeholder="Short summary of the issue"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={SUBJECT_MAX}
            error={subjectError ?? undefined}
            required
          />

          <div>
            <label
              htmlFor="ticket-description"
              className="block text-sm font-medium text-neutral-700"
            >
              Description
            </label>
            <div className="mt-1">
              <RichTextEditor
                value={descriptionHtml}
                onChange={(html, text) => {
                  setDescriptionHtml(html);
                  setDescriptionText(text);
                }}
                placeholder="Describe what happened, what you expected, and any steps to reproduce."
                minHeight="10rem"
                id="ticket-description"
                ariaLabel="Description"
              />
            </div>
            {descriptionError && (
              <p className="mt-1 text-xs text-red-600">{descriptionError}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Select
              id="ticket-priority"
              label="Priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
              options={PRIORITY_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
            <Select
              id="ticket-type"
              label="Type"
              value={type}
              onChange={(e) => setType(e.target.value as TicketType)}
              options={TYPE_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
              placeholder="Select a type"
            />
          </div>

          <div>
            <label
              htmlFor="ticket-attachments"
              className="block text-sm font-medium text-neutral-700"
            >
              Attachments
            </label>
            <div className="mt-1" id="ticket-attachments">
              <AttachmentDropzone value={files} onChange={setFiles} />
            </div>
          </div>

          <Input
            id="ticket-cc"
            label="CC emails (optional)"
            placeholder="alice@example.com, bob@example.com"
            value={ccRaw}
            onChange={(e) => setCcRaw(e.target.value)}
            helperText="Comma-separated. They'll see replies on this ticket."
            error={ccError ?? undefined}
          />

          {submitError && <Alert variant="error">{submitError}</Alert>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/tickets")}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={
                createTicket.isPending || createTicketAsAgent.isPending
              }
            >
              Submit Ticket
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export default NewTicketPage;
